/**
 * The workspace's stickers for this person: the library (packs, "My stickers" order, settings, permissions), recents,
 * the GIF provider config, the "Add stickers" browser and the pack sheet opened from a sticker in a message. One per
 * workspace (StickersContext), shared by every composer's picker and the sticker creator.
 *
 * The library is cached on the device (KeyValueStorage) so the picker opens instantly and offline; it refreshes when a
 * picker opens. "My stickers" edits are optimistic and rolled back (with a notice) when the server refuses them.
 */
import type {
  GifConfigResponse,
  MediaSettings,
  Sticker,
  StickerLibraryResponse,
  StickerPack,
  StickerPackScope,
  UpdateMediaSettingsRequest,
} from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import {
  addPack,
  browsePacks,
  canDeletePack,
  canEditPack,
  DEFAULT_MEDIA_SETTINGS,
  MAX_RECENT_STICKERS,
  movePack,
  packNameProblem,
  pushRecent,
  removePack,
  type BrowseEntry,
} from "../logic/stickers.ts";
import { errorKind, type Clock, type GifTransport, type IdGen, type KeyValueStorage, type StickerImageProcessor, type StickersApi } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** Platform services for stickers and GIFs (web: platform/stickers; iOS/Android: hand-written adapters). */
export type MediaDeps = {
  api: StickersApi;
  gifs: GifTransport;
  images: StickerImageProcessor;
  /** App language ("en", "pt-BR"): the GIF provider's locale. */
  locale: () => string;
};

/** The pack sheet (from a sticker in a message, or a row of the browser). */
export type PackView = {
  packId: string;
  status: "loading" | "ready" | "error";
  pack?: StickerPack;
  /** In "My stickers". */
  added: boolean;
  /** I may rename it / add to it. */
  canEdit: boolean;
  canDelete: boolean;
  /** An admin may move it between personal and workspace. */
  canChangeScope: boolean;
};

export type StickersState = {
  status: "loading" | "ready" | "error";
  packs: StickerPack[];
  myPackIds: string[];
  settings: MediaSettings;
  canCreate: boolean;
  canManage: boolean;
  /** Recently sent sticker ids, newest first (this device). */
  recent: string[];
  /** "Add stickers" rows. */
  browse: BrowseEntry[];
  /** GIF config: `undefined` until fetched (the picker then shows a spinner, offline "GIFs unavailable"). */
  gif?: GifConfigResponse;
  /** The GIF config couldn't be fetched (offline / server error). */
  gifError: boolean;
  viewing?: PackView;
  /** The "Stickers" manager sheet (My stickers order, workspace packs, admin settings) is open. */
  managing: boolean;
  /** Last failure to show as a toast. */
  notice?: MessageDescriptor;
};

const LIBRARY_KEY = "bundle.stickers.library.";
const RECENT_KEY = "bundle.stickers.recent.";

export class StickersViewModel extends ViewModel<StickersState> {
  readonly ws: WorkspaceViewModel;
  readonly media: MediaDeps;
  private loading: Promise<void> | undefined;
  private gifLoading: Promise<void> | undefined;

  constructor(ws: WorkspaceViewModel, media: MediaDeps) {
    const storage = ws.deps.storage;
    // Per workspace and person (a shared device keeps each person's library and recents apart).
    const scope = `${ws.wsId}.${ws.state.meId}`;
    const cached = parseLibrary(storage.get(`${LIBRARY_KEY}${scope}`));
    const recent = parseIds(storage.get(`${RECENT_KEY}${scope}`));
    const initial: StickersState = {
      status: cached === undefined ? "loading" : "ready",
      packs: cached?.packs ?? [],
      myPackIds: cached?.myPackIds ?? [],
      settings: cached?.settings ?? DEFAULT_MEDIA_SETTINGS,
      canCreate: cached?.canCreate ?? false,
      canManage: cached?.canManage ?? false,
      recent,
      browse: [],
      gifError: false,
      managing: false,
    };
    super(initial);
    this.ws = ws;
    this.media = media;
    this.setState({ ...this.state, browse: browsePacks(this.state.packs, this.state.myPackIds, ws.state.meId) });
  }

  private get scope(): string {
    return `${this.ws.wsId}.${this.ws.state.meId}`;
  }

  private get storage(): KeyValueStorage {
    return this.ws.deps.storage;
  }
  get clock(): Clock {
    return this.ws.deps.clock;
  }
  get ids(): IdGen {
    return this.ws.deps.ids;
  }

  /** Fetches the library (deduped while one is in flight). */
  refresh(): Promise<void> {
    this.loading ??= this.load().finally(() => {
      this.loading = undefined;
    });
    return this.loading;
  }

  private async load(): Promise<void> {
    try {
      const lib = await this.media.api.library(this.ws.wsId);
      this.applyLibrary(lib);
    } catch {
      if (this.state.status === "loading") this.update({ status: "error" });
    }
  }

  /** Fetches the GIF config once per session (again after a failure). */
  loadGifConfig(): Promise<void> {
    if (this.state.gif !== undefined) return Promise.resolve();
    this.gifLoading ??= (async () => {
      try {
        const gif = await this.media.api.gifConfig(this.ws.wsId);
        this.update({ gif, gifError: false });
      } catch {
        this.update({ gifError: true });
      } finally {
        this.gifLoading = undefined;
      }
    })();
    return this.gifLoading;
  }

  private applyLibrary(lib: StickerLibraryResponse): void {
    this.storage.set(`${LIBRARY_KEY}${this.scope}`, JSON.stringify(lib));
    this.setLibrary({ packs: lib.packs, myPackIds: lib.myPackIds, settings: lib.settings, canCreate: lib.canCreate, canManage: lib.canManage, status: "ready" });
  }

  private setLibrary(patch: Partial<StickersState>): void {
    const next: StickersState = { ...this.state, ...patch };
    next.browse = browsePacks(next.packs, next.myPackIds, this.ws.state.meId);
    if (next.viewing !== undefined && next.viewing.pack !== undefined) {
      const pack = next.packs.find((p) => p.id === next.viewing!.packId) ?? next.viewing.pack;
      next.viewing = this.packView(pack, next);
    }
    this.setState(next);
    this.persist();
  }

  private persist(): void {
    const s = this.state;
    const lib: StickerLibraryResponse = { packs: s.packs, myPackIds: s.myPackIds, settings: s.settings, canCreate: s.canCreate, canManage: s.canManage };
    this.storage.set(`${LIBRARY_KEY}${this.scope}`, JSON.stringify(lib));
  }

  private packView(pack: StickerPack, s: StickersState = this.state): PackView {
    const me = this.ws.state.cache?.members[this.ws.state.meId];
    const now = this.clock.now();
    return {
      packId: pack.id,
      status: "ready",
      pack,
      added: s.myPackIds.includes(pack.id),
      canEdit: me !== undefined && canEditPack(pack, me, s.settings, now),
      canDelete: me !== undefined && canDeletePack(pack, me, s.settings, now),
      canChangeScope: s.canManage,
    };
  }

  // ---------- Recents ----------

  /** A sticker was sent: it moves to the front of recents (this device). */
  used(stickerId: string): void {
    const recent = pushRecent(this.state.recent, stickerId, MAX_RECENT_STICKERS);
    this.storage.set(`${RECENT_KEY}${this.scope}`, JSON.stringify(recent));
    this.update({ recent });
  }

  /** The sticker by id among the packs I can see. */
  sticker(id: string): Sticker | undefined {
    for (const p of this.state.packs) for (const s of p.stickers) if (s.id === id) return s;
    if (this.state.viewing?.pack !== undefined) return this.state.viewing.pack.stickers.find((s) => s.id === id);
    return undefined;
  }

  // ---------- My stickers ----------

  addPack(packId: string): void {
    this.writeMine(addPack(this.state.myPackIds, packId), packId);
  }

  removePack(packId: string): void {
    this.writeMine(removePack(this.state.myPackIds, packId));
  }

  movePack(packId: string, toIndex: number): void {
    this.writeMine(movePack(this.state.myPackIds, packId, toIndex));
  }

  private writeMine(next: string[], added?: string): void {
    const before = this.state.myPackIds;
    if (next === before) return;
    // A pack opened from a message isn't in the library yet: keep it so "My stickers" can show it at once.
    let packs = this.state.packs;
    const viewed = this.state.viewing?.pack;
    if (added !== undefined && viewed !== undefined && viewed.id === added && !packs.some((p) => p.id === added)) packs = [...packs, viewed];
    this.setLibrary({ myPackIds: next, packs });
    void this.media.api.setMyPacks(this.ws.wsId, next).then(
      (saved) => {
        if (this.state.myPackIds === next) this.setLibrary({ myPackIds: saved });
      },
      (e: unknown) => {
        if (this.state.myPackIds === next) this.setLibrary({ myPackIds: before });
        this.update({ notice: errorKind(e) === "network" ? msg("stickers.notice.offline") : msg("stickers.notice.failed") });
      },
    );
  }

  // ---------- Manager sheet ----------

  openManager(): void {
    this.update({ managing: true });
    void this.refresh();
  }

  closeManager(): void {
    this.update({ managing: false });
  }

  // ---------- Pack sheet ----------

  /** Opens a pack (from a sticker in a message): from the library when known, else fetched. */
  openPack(packId: string): void {
    const known = this.state.packs.find((p) => p.id === packId);
    if (known !== undefined) {
      this.update({ viewing: this.packView(known) });
      void this.refreshPack(packId);
      return;
    }
    this.update({ viewing: { packId, status: "loading", added: this.state.myPackIds.includes(packId), canEdit: false, canDelete: false, canChangeScope: false } });
    void this.refreshPack(packId);
  }

  private async refreshPack(packId: string): Promise<void> {
    try {
      const pack = await this.media.api.pack(this.ws.wsId, packId);
      if (this.state.viewing?.packId !== packId) return;
      const known = this.state.packs.some((p) => p.id === packId);
      if (known) this.setLibrary({ packs: this.state.packs.map((p) => (p.id === packId ? pack : p)) });
      this.update({ viewing: this.packView(pack) });
    } catch {
      const v = this.state.viewing;
      if (v === undefined || v.packId !== packId || v.status === "ready") return;
      this.update({ viewing: { ...v, status: "error" } });
    }
  }

  closePack(): void {
    if (this.state.viewing === undefined) return;
    const { viewing: _v, ...rest } = this.state;
    this.setState(rest);
  }

  // ---------- Creating and curating (online) ----------

  /** Creates a pack and adds it to "My stickers". Throws ApiFailure; validation problems as MessageDescriptor notices. */
  async createPack(name: string, scope: StickerPackScope): Promise<StickerPack | undefined> {
    const problem = packNameProblem(name);
    if (problem !== null) {
      this.update({ notice: problem });
      return undefined;
    }
    const pack = await this.media.api.createPack(this.ws.wsId, { name: name.trim(), scope, clientId: this.ids.next() });
    this.upsertPack(pack);
    if (!this.state.myPackIds.includes(pack.id)) this.addPack(pack.id);
    return pack;
  }

  /** A sticker was created in `packId` (StickerCreatorViewModel). */
  stickerCreated(sticker: Sticker): void {
    const pack = this.state.packs.find((p) => p.id === sticker.packId);
    if (pack !== undefined && !pack.stickers.some((s) => s.id === sticker.id)) this.upsertPack({ ...pack, stickers: [...pack.stickers, sticker] });
    if (!this.state.myPackIds.includes(sticker.packId)) this.addPack(sticker.packId);
    void this.refresh();
  }

  private upsertPack(pack: StickerPack): void {
    const exists = this.state.packs.some((p) => p.id === pack.id);
    this.setLibrary({ packs: exists ? this.state.packs.map((p) => (p.id === pack.id ? pack : p)) : [...this.state.packs, pack] });
    if (this.state.viewing?.packId === pack.id) this.update({ viewing: this.packView(pack) });
  }

  async renamePack(packId: string, name: string): Promise<void> {
    const problem = packNameProblem(name);
    if (problem !== null) {
      this.update({ notice: problem });
      return;
    }
    await this.guard(async () => this.upsertPack(await this.media.api.updatePack(this.ws.wsId, packId, { name: name.trim() })));
  }

  /** Admins: workspace ⇄ personal. */
  async setPackScope(packId: string, scope: StickerPackScope): Promise<void> {
    await this.guard(async () => this.upsertPack(await this.media.api.updatePack(this.ws.wsId, packId, { scope })));
  }

  async deletePack(packId: string): Promise<void> {
    await this.guard(async () => {
      await this.media.api.deletePack(this.ws.wsId, packId);
      this.setLibrary({ packs: this.state.packs.filter((p) => p.id !== packId), myPackIds: removePack(this.state.myPackIds, packId) });
      if (this.state.viewing?.packId === packId) this.closePack();
    });
  }

  async deleteSticker(stickerId: string): Promise<void> {
    await this.guard(async () => {
      await this.media.api.deleteSticker(this.ws.wsId, stickerId);
      this.setLibrary({ packs: this.state.packs.map((p) => ({ ...p, stickers: p.stickers.filter((s) => s.id !== stickerId) })) });
      const v = this.state.viewing;
      if (v?.pack !== undefined) this.update({ viewing: this.packView({ ...v.pack, stickers: v.pack.stickers.filter((s) => s.id !== stickerId) }) });
    });
  }

  /** Admins: who may create stickers, GIF filter, GIFs on/off. */
  async updateSettings(patch: UpdateMediaSettingsRequest): Promise<void> {
    const before = this.state.settings;
    this.setLibrary({ settings: { ...before, ...patch } });
    try {
      const settings = await this.media.api.updateSettings(this.ws.wsId, patch);
      this.setLibrary({ settings });
      // The GIF filter/availability changed: fetch the config again when the picker next opens.
      if (patch.gifContentFilter !== undefined || patch.gifsEnabled !== undefined) {
        const { gif: _g, ...rest } = this.state;
        this.setState(rest);
      }
      void this.refresh();
    } catch (e) {
      this.setLibrary({ settings: before });
      this.update({ notice: errorKind(e) === "network" ? msg("stickers.notice.offline") : msg("stickers.notice.failed") });
    }
  }

  dismissNotice(): void {
    if (this.state.notice === undefined) return;
    const { notice: _n, ...rest } = this.state;
    this.setState(rest);
  }

  /** Shows a notice for a failed REST call instead of throwing. */
  notify(e: unknown): void {
    this.update({ notice: errorKind(e) === "network" ? msg("stickers.notice.offline") : errorKind(e) === "forbidden" ? msg("stickers.notice.forbidden") : msg("stickers.notice.failed") });
  }

  private async guard(fn: () => Promise<void>): Promise<void> {
    try {
      await fn();
    } catch (e) {
      this.notify(e);
    }
  }
}

function parseLibrary(raw: string | undefined): StickerLibraryResponse | undefined {
  if (raw === undefined) return undefined;
  try {
    const v = JSON.parse(raw) as StickerLibraryResponse;
    return Array.isArray(v.packs) && Array.isArray(v.myPackIds) && v.settings !== undefined ? v : undefined;
  } catch {
    return undefined;
  }
}

function parseIds(raw: string | undefined): string[] {
  if (raw === undefined) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
